import './Notifier.scss';
import { useEffect, useState, useContext, createContext } from 'react';

const NotifierContext = createContext();

export const useNotifier = () => {
    const context = useContext(NotifierContext);
    if (!context) {
        throw new Error('useNotifier must be used within a NotifierProvider');
    }
    return context;
};

export const NotifierProvider = ({ children }) => {
    const [isOpen, setOpen] = useState(false);
    const [isVisible, setVisible] = useState(false);
    const [content, setContent] = useState(null);
    const [isPaused, setIsPaused] = useState(false);
    
    const isNotificationDisabled = () => {
        const savedDisabledUntil = localStorage.getItem('notifier-disabled-until');
        if (!savedDisabledUntil) return false;
        
        const until = new Date(savedDisabledUntil);
        const now = new Date();
        
        if (now < until) {
            return true;
        } else {
            localStorage.removeItem('notifier-disabled-until');
            return false;
        }
    };
    
    const showNotifier = (content) => {
        if (isNotificationDisabled()) return;
        
        setContent(content);
        setOpen(true);
        setTimeout(() => setVisible(true), 10);
    };
    
    const hideNotifier = () => {
        setVisible(false);
        setTimeout(() => setOpen(false), 300);
    };
    
    const pauseTimer = () => {
        setIsPaused(true);
    };
    
    const resumeTimer = () => {
        setIsPaused(false);
    };
    
    const disableFor1Hour = () => {
        const until = new Date();
        until.setHours(until.getHours() + 1);
        localStorage.setItem('notifier-disabled-until', until.toISOString());
        hideNotifier();
    };
    
    const disableUntilTomorrow = () => {
        const until = new Date();
        until.setDate(until.getDate() + 1);
        until.setHours(5, 0, 0, 0);
        localStorage.setItem('notifier-disabled-until', until.toISOString());
        hideNotifier();
    };
    
    const enableNotifications = () => {
        localStorage.removeItem('notifier-disabled-until');
    };
    
    useEffect(() => {
        if (isOpen && !isPaused) {
            const timer = setTimeout(() => {
                hideNotifier();
            }, 5000);
            
            return () => clearTimeout(timer);
        }
    }, [isOpen, isPaused, content]); // Added content to dependencies
    
    return (
        <NotifierContext.Provider value={{ 
            showNotifier, 
            hideNotifier, 
            disableFor1Hour, 
            disableUntilTomorrow, 
            enableNotifications,
            isNotificationDisabled
        }}>
            {children}
            {isOpen && <Notifier 
                content={content}
                hideNotifier={hideNotifier}
                isVisible={isVisible} 
                onMouseEnter={pauseTimer} 
                onMouseLeave={resumeTimer}
                onDisableFor1Hour={disableFor1Hour}
                onDisableUntilTomorrow={disableUntilTomorrow}
            />}
        </NotifierContext.Provider>
    );
};

const Notifier = ({ content, isVisible, onMouseEnter, onMouseLeave, onDisableFor1Hour, onDisableUntilTomorrow, hideNotifier }) => {
    const [showOptions, setShowOptions] = useState(false);
    
    return (
        <div 
            className={`notifier-wrapper ${isVisible ? 'notifier-visible' : 'notifier-hidden'}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="notifier-content">
                {content || 'Notification'}
            </div>
            <div className="notifier-actions">
                <button 
                    className="notifier-options-btn"
                    onClick={() => setShowOptions(!showOptions)}
                    title="Options de notification"
                >
                    Silence
                </button>
                <button 
                    className="notifier-close-btn" 
                    onClick={hideNotifier} 
                    title="Fermer cette notification"
                >
                    Fermer
                </button>
            </div>
            
            {showOptions && (
                <div className="notifier-options">
                    <button 
                        className="notifier-option-btn"
                        onClick={onDisableFor1Hour}
                    >
                        Silence 1h
                    </button>
                    <button 
                        className="notifier-option-btn"
                        onClick={onDisableUntilTomorrow}
                    >
                        Jusqu'à demain
                    </button>
                </div>
            )}
        </div>
    );
};